import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowCircleDown } from '@mui/icons-material';
import {
  MONITOR_SIDEBAR_MODULE,
  MonitorSideBar,
  PAGE_COMMON_TEXT,
  StudentApplication,
  SupervisorNomination,
  DSRCAssessmentForm,
  HoDDoSAssessmentForm,
  FRCAssessmentForm,
  DSRCConditionCheckForm,
  GSAssessmentForm,
  QualificationCheckView,
  VisaApplicationView,
  ScholarshipEvaluation,
} from './components';
import { DSRCApprovalForm } from './components/DSRCApproval/DSRCApprovalForm';
import { useGetApplicationStatus } from './graphql';
import { Alert, Button, ErrorPage, Loading } from '../../components';
import { OneOffDialog, type OneOffDialogConfig } from '../Assessment';

const SIDEBAR_TEXT = PAGE_COMMON_TEXT.SIDEBAR_TEXT;
export const Monitor: React.FunctionComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { applicationId = '' } = useParams();

  const { status, applicationNo, loading, error } =
    useGetApplicationStatus(applicationId);

  const searchParams = new URLSearchParams(location.search);
  /* am means Activated Module */
  let sidebarActivated = searchParams.get('am') as MONITOR_SIDEBAR_MODULE;

  if (!Object.values(MONITOR_SIDEBAR_MODULE).includes(sidebarActivated)) {
    sidebarActivated = MONITOR_SIDEBAR_MODULE.STUDENT_APPLICATION;
  }

  const [oneOffDialogConfig, setOpenOneOffDialog] =
    useState<OneOffDialogConfig>({
      open: false,
      applicationId: '',
      applicationNo: '',
    });

  const [activatedModuleId, setActivatedModuleId] = useState(sidebarActivated);

  useEffect(() => {
    searchParams.set('am', activatedModuleId);
    // Update the URL with new Module
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [activatedModuleId]);

  const handleChangeActivated = (activatedId: MONITOR_SIDEBAR_MODULE): void => {
    setActivatedModuleId(activatedId);
  };
  const monitorModulesMap: Partial<
    Record<MONITOR_SIDEBAR_MODULE, { component: any; moduleTitle: string }>
  > = {
    [MONITOR_SIDEBAR_MODULE.STUDENT_APPLICATION]: {
      component: <StudentApplication />,
      moduleTitle: SIDEBAR_TEXT.STUDENT_APPLICATION,
    },
    [MONITOR_SIDEBAR_MODULE.SUPERVISOR_NOMINATION]: {
      component: <SupervisorNomination />,
      moduleTitle: SIDEBAR_TEXT.SUPERVISOR_NOMINATION,
    },

    [MONITOR_SIDEBAR_MODULE.DORPGS_ASSESSMENT]: {
      component: <DSRCAssessmentForm />,
      moduleTitle: SIDEBAR_TEXT.DORPGS_ASSESSMENT,
    },
    [MONITOR_SIDEBAR_MODULE.SCHOLARSHIP_EVALUATION]: {
      component: <ScholarshipEvaluation />,
      moduleTitle: SIDEBAR_TEXT.SCHOLARSHIP_EVALUATION.TITLE,
    },
    [MONITOR_SIDEBAR_MODULE.DRC_APPROVAL]: {
      component: <DSRCApprovalForm />,
      moduleTitle: SIDEBAR_TEXT.DRC_APPROVAL,
    },
    [MONITOR_SIDEBAR_MODULE.HOD_DOS_ASSESSMENT]: {
      component: <HoDDoSAssessmentForm />,
      moduleTitle: SIDEBAR_TEXT.HOD_DOS_ASSESSMENT,
    },
    [MONITOR_SIDEBAR_MODULE.FRC_ASSESSMENT]: {
      component: <FRCAssessmentForm />,
      moduleTitle: SIDEBAR_TEXT.FRC_ASSESSMENT,
    },
    // [MONITOR_SIDEBAR_MODULE.PANEL_SELECTION]: {
    //   component: waitingForProcessing(SIDEBAR_TEXT.PANEL_SELECTION),
    //   moduleTitle: SIDEBAR_TEXT.PANEL_SELECTION,
    // },
    [MONITOR_SIDEBAR_MODULE.GS_DECISION]: {
      component: <GSAssessmentForm />,
      moduleTitle: SIDEBAR_TEXT.GS_DECISION,
    },
    [MONITOR_SIDEBAR_MODULE.CONDITION_CHECK]: {
      component: <DSRCConditionCheckForm />,
      moduleTitle: SIDEBAR_TEXT.CONDITION_CHECK,
    },
    [MONITOR_SIDEBAR_MODULE.QUALIFICATION_CHECK]: {
      component: <QualificationCheckView />,
      moduleTitle: SIDEBAR_TEXT.QUALIFICATION_CHECK,
    },
    [MONITOR_SIDEBAR_MODULE.VISA_APPLICATION]: {
      component: <VisaApplicationView />,
      moduleTitle: SIDEBAR_TEXT.VISA_APPLICATION,
    },
  };

  const activatedModule:
    | { component: React.ReactNode; moduleTitle: string }
    | undefined = monitorModulesMap[activatedModuleId];

  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;

  if (!loading && status === '') {
    return <ErrorPage errorCode={200} />;
  }

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const { open: openDialog } = oneOffDialogConfig;

  return (
    <>
      {openDialog && (
        <OneOffDialog
          openDialogConfig={oneOffDialogConfig}
          onDialogClose={() => {
            setOpenOneOffDialog({
              open: false,
              applicationId: '',
              applicationNo: '',
            });
          }}
        />
      )}
      {loading ? (
        <Loading />
      ) : (
        <PageContainer>
          <div className="sidebar-area">
            <MonitorSideBar
              onChangeActivated={handleChangeActivated}
              activatedModule={activatedModuleId}
            />
          </div>
          <Container>
            <div className="w-full">
              <div className="flex justify-end w-full pr-6 pt-4">
                <Button
                  sx={{ textTransform: 'none' }}
                  startIcon={<ArrowCircleDown />}
                  onClick={() => {
                    setOpenOneOffDialog({
                      open: true,
                      applicationId,
                      applicationNo,
                    });
                  }}
                >
                  Download the Application Profile
                </Button>
              </div>
              <h1>{moduleTitle}</h1>
            </div>
            <div className="form-area">{moduleComponent}</div>
          </Container>
        </PageContainer>
      )}
    </>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;
  h1 {
    margin-top: 1rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    color: #951d1d;
  }
  .sidebar-area {
    width: min-content;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .form-area {
    margin: 1rem;
  }
`;
